import { Button, Col, Dropdown, Menu, Row } from "antd";
import { useCallback, useMemo } from "react";
import { useAssetBatchesContext } from "screens/assetExporter/feedConfigurationV2/shared/contexts/AssetBatchesContext";
import {
  TValueMapping,
  TVariable,
} from "screens/assetExporter/feedConfigurationV2/shared/types";
import { useDataList } from "shared/components/dataList";
import { useLogoSelectContext } from "shared/components/logoSelect/context/LogoSelectContext";
import { CAMSelect } from "shared/components/media";
import { BrandLogo, CAMLogo } from "shared/types/brandsAccounts";
import { FieldTitle } from "../../shared/components/FieldTitle";
import { useAssetBatchesTemplateCompositionContext } from "../../shared/contexts/AssetBatchesTemplateCompositionContext";
import { isLogoValue } from "../../shared/validators";
import { getLogoArtboardName } from "../utils";
import styles from "./VariableInput.module.scss";
import { useHightlightVarEvents } from "./hooks/useHighlightVarEvents";

type LogoVariableInputProps = {
  variable: TVariable;
  valueMapping?: TValueMapping;
  mappingKey: string;
};

export const LogoVariableInput = ({
  variable,
  valueMapping,
  mappingKey,
}: LogoVariableInputProps) => {
  const { templatesToUse: templates } = useAssetBatchesContext();
  const { onLogoSelect, editingComposition } =
    useAssetBatchesTemplateCompositionContext();
  const template = templates.find(
    temp => editingComposition.template === temp.id,
  );
  const { onMouseEnter, onMouseLeave, onValueChange } =
    useHightlightVarEvents(mappingKey);
  const { setShowLogoSelectDrawer, setOnSelect } = useLogoSelectContext();
  const [, dataListActions] = useDataList<BrandLogo>();

  const setLogoSelectFilters = useCallback(() => {
    dataListActions.resetFilters();
    const filters: Record<string, string[]> = {
      brand: [...(template?.oems ?? [])],
      account: [...(template?.stores ?? [])],
      logoType: variable?.logoData?.logoType
        ? [variable.logoData.logoType.toString()]
        : [],
      artboardName: variable?.logoData?.artboardName
        ? [variable.logoData.artboardName.toString()]
        : [],
    };
    const updatedFilters = Object.keys(filters).reduce(
      (acc: Record<string, string[]>, key: string) => {
        if (filters[key].length) return { ...acc, [key]: filters[key] };
        return acc;
      },
      {},
    );
    dataListActions.setFilters(updatedFilters);
  }, [dataListActions, template, variable]);

  const setOnSelectLogo = useCallback(
    (logo: BrandLogo | CAMLogo) => {
      onLogoSelect(mappingKey, logo);
      const hasValue =
        (valueMapping?.value && isLogoValue(valueMapping.value)) || false;
      onValueChange(mappingKey, hasValue);
    },
    [mappingKey, onLogoSelect, onValueChange, valueMapping?.value],
  );

  const showLogoDrawer = useCallback(() => {
    setShowLogoSelectDrawer(true);
    setLogoSelectFilters();
    setOnSelect(() => setOnSelectLogo);
    if (valueMapping?.value && isLogoValue(valueMapping.value)) {
      dataListActions.setSelectedItems([valueMapping.value.id]);
    }
  }, [
    setShowLogoSelectDrawer,
    setLogoSelectFilters,
    setOnSelect,
    setOnSelectLogo,
    valueMapping?.value,
    dataListActions,
  ]);

  const displayName = isLogoValue(valueMapping?.value)
    ? `${valueMapping?.value.name} - ${valueMapping?.value.logoType} - ${valueMapping?.value?.artboardName}`
    : "";

  const variableName =
    variable.variable !== ""
      ? variable.variable
      : `${variable.logoData?.logoType} - ${variable.logoData?.artboardName} Logo`;

  const options = useMemo(
    () => [
      {
        key: "brandAndAccount",
        children: "From Brands and Accounts",
        onClick: () => showLogoDrawer(),
      },
      {
        key: "cam",
        children: (
          <CAMSelect
            width="95vw"
            layout="grid"
            title={<b>Select logo from CAM</b>}
            maxSelected={1}
            fileTypes={["png", "jpeg", "jpg"]}
            onClose={async assets => {
              const artboardName = getLogoArtboardName(
                assets[0].width,
                assets[0].height,
              );
              const logoFromCam: CAMLogo = {
                id: assets[0].id,
                name: assets[0].name,
                logoUrl: assets[0].hiResURLRaw,
                logoType: "CAM",
                artboardName,
              };
              setOnSelectLogo(logoFromCam);
            }}
            search={{ query: "(.png OR .jpeg OR .jpg) AND logo" }}
          >
            <div>From CAM</div>
          </CAMSelect>
        ),
      },
    ],
    [setOnSelectLogo, showLogoDrawer],
  );

  const menu = (
    <Menu>
      {options.map(({ key, children, onClick }) => (
        <Menu.Item key={key} onClick={onClick}>
          {children}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <>
      <FieldTitle title={variableName} />
      <Row
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={styles.row}
        justify="space-around"
        align="top"
      >
        <Col span={24}>
          <Dropdown overlay={menu}>
            <Button>{valueMapping?.value ? "Change" : "Select"}</Button>
          </Dropdown>
          <span className={styles.logoSelectName}>{displayName}</span>
        </Col>
      </Row>
    </>
  );
};
